/**
 * What a WHERE / HAVING chip on one column needs from the rest of the query —
 * what Table Explorer's chip gets from its grid and its filter bar:
 *  - `scanFilters`: the statement's other WHERE conditions on the same table
 *    (the AND-joined ones that reopen as chip conditions; the rest are
 *    skipped), so the JSON peek scans only the rows the query reads;
 *  - `sampleRows`: the tab's last results for the column, for the
 *    Declare-as-JSON preview (the chip reads a small sample itself when they
 *    don't carry it).
 */

import { useMemo } from 'react';

import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import { conjunctLeaves, foldWholeGroups } from '../../../../sql-resolver/edit/wholePredicate';
import { useQueryTabColumnSample } from '../../../common/hooks/useQueryTabColumnSample';
import type { FilterGroup } from '../../../core/types/workspaceTypes';
import { extractStructureForContext, type VqeEditContext } from '../../edit/editContext';
import { enrichParsedCondition, isWholeChipFilter, resolveEditPredicate } from './looseFkEditEnrichment';
import type { ResolvedFilterColumn, VqeFilterColumns } from './useVqeFilterColumns';

/** Same table under the same alias (`quoteSiblingField` carries the query's qualifier). */
const sameSource = (a: ResolvedFilterColumn, b: ResolvedFilterColumn): boolean =>
  a.schema === b.schema && a.table === b.table && a.quoteSiblingField?.('') === b.quoteSiblingField?.('');

/** The chip conditions among `texts` on `target`'s table, as Table Explorer filters (bare column names). */
export function chipScanFilters(
  texts: readonly string[],
  engine: DatabaseEngineId,
  resolveByField: VqeFilterColumns['resolveByField'],
  target: ResolvedFilterColumn,
): FilterGroup[] {
  return texts.flatMap((text) => {
    const hit = resolveEditPredicate(text, engine, resolveByField);
    if (!hit?.resolved || !sameSource(hit.resolved, target)) return [];
    const { condition } = enrichParsedCondition(hit.parsed, hit.resolved, engine);
    return [{ field: hit.resolved.column.name, conditions: [condition] }];
  });
}

export function useVqeChipDataScope(
  editContext: VqeEditContext,
  cols: VqeFilterColumns,
  target: ResolvedFilterColumn | null,
  /** The condition the chip edits — never a filter on itself. */
  ownPredicate?: string,
): { readonly sampleRows?: Record<string, unknown>[]; readonly scanFilters?: FilterGroup[] } {
  const sampleRows = useQueryTabColumnSample(
    target ? editContext.tabId : undefined,
    target ? { schema: target.schema, table: target.table, column: target.column.name } : null,
  );
  // Read only while a chip is open on a table column — the idle "+ Add" builders never parse.
  const source = target?.table ? editContext.service.getParsedBuffer(editContext.documentId)?.parsedDocument.source : undefined;
  const { engine, resolveByField } = cols;
  const scanFilters = useMemo(() => {
    if (!target || source === undefined) return undefined;
    try {
      const tree = extractStructureForContext(editContext).whereTree;
      if (!tree) return undefined;
      const own = ownPredicate?.trim();
      const texts = conjunctLeaves(foldWholeGroups(tree, source, (t) => isWholeChipFilter(t, editContext)))
        .map((leaf) => leaf.text)
        .filter((text) => text.trim() !== own);
      return chipScanFilters(texts, engine, resolveByField, target);
    } catch {
      return undefined;
    }
  }, [editContext, source, target, ownPredicate, engine, resolveByField]);
  return { sampleRows, scanFilters };
}
